import { BlockComponentFactory } from '../BlockFactory'
import Checkbox from 'antd/lib/checkbox'
import { CheckboxBlockProps } from './block-props.types'
import { LabelBlock } from './LabelBlock'
import { LABEL_FONT_STYLE } from '../../constants'
import { useMemo } from 'react'
import { useBlockListeners } from '../BlockHooks'
import { useBlockEngine } from '../BlockEngineContext'
import { toCSSSize } from './block-utils'

const OPTION_LABEL_FONT_STYLE: React.CSSProperties = {
    ...LABEL_FONT_STYLE,
    fontSize: '12px',
}

export const CheckboxBlockComponent =
    BlockComponentFactory.createBlockComponent<CheckboxBlockProps>(props => {
        const {
            label,
            infoText,
            width,
            visibility = 'visible',
            disabled = false,
            options = [],
            direction = 'vertical',
            style,
            value,
            required,
        } = props

        const isEditable = useBlockEngine().blockEngine.getEditableSignal().value

        const [onChangeListener] = useBlockListeners<(value: string[]) => void>('onChange')

        const containerStyle = useMemo<React.CSSProperties>(
            () => ({
                ...style,
                display: visibility === 'hidden' ? 'none' : 'flex',
                flexDirection: 'column',
                width: toCSSSize(width) ?? 'auto',
                gap: '8px',
            }),
            [style, visibility, width]
        )

        const groupStyle = useMemo<React.CSSProperties>(
            () => ({
                display: 'flex',
                flexDirection: direction === 'horizontal' ? 'row' : 'column',
                gap: direction === 'horizontal' ? '16px' : '8px',
            }),
            [direction]
        )

        const normalizedValue = useMemo<string[] | undefined>(() => {
            if (Array.isArray(value)) return value

            if (typeof value === 'string') {
                return value
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean)
            }

            return undefined
        }, [value])

        const defaultCheckedValues = useMemo<string[]>(
            () => options.filter(option => option.checked).map(option => option.value),
            [options]
        )

        const evaluatedValue = normalizedValue ?? defaultCheckedValues

        const checkboxOptions = useMemo(
            () =>
                options.map(option => ({
                    label: option.value,
                    value: option.value,
                    style: OPTION_LABEL_FONT_STYLE,
                })),
            [options]
        )

        const handleChange = (checkedValues: string[]) => {
            onChangeListener?.value?.(checkedValues)
        }

        if (visibility === 'hidden') {
            return null
        }

        return (
            <div style={containerStyle}>
                <LabelBlock
                    required={required}
                    label={label}
                    infoText={infoText}
                    labelStyle={LABEL_FONT_STYLE}
                />

                <Checkbox.Group
                    value={evaluatedValue}
                    options={checkboxOptions}
                    style={groupStyle}
                    disabled={!isEditable || disabled}
                    onChange={handleChange}
                />
            </div>
        )
    })
